import { useMemo, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  Box,
  CheckCircle2,
  Database,
  PackageSearch,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Ruler,
  Save,
  Settings2,
  ShieldCheck,
  Truck,
} from 'lucide-react'
import { toast } from 'sonner'
import { ShipmentAccuracyTab } from '@/pages/shipment-accuracy-tab'
import { useProductShippingFacts } from '@/api/hooks'
import type { components } from '@/api/schema'
import {
  type ShippingPolicy,
  type ShippingPolicyUpdate,
  type ShippingSimulatorRequest,
  useCreateShippingOrigin,
  useCreateShippingPolicyDraft,
  useCreateShippingRateCard,
  useNormalizeShippingOrigins,
  usePublishShippingPolicy,
  usePublishShippingRateCard,
  useRebuildShippingEstimates,
  useRebuildShippingRateCard,
  useRollbackShippingPolicy,
  useShippingOrigins,
  useShippingPolicies,
  useShippingQuoteAttempts,
  useShippingRateCards,
  useShippingReadiness,
  useSimulateShippingQuote,
  useUpdateShippingPolicy,
  useUpsertShippingOverride,
} from '@/api/shipping-calculator'
import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import {
  CatalogProductPicker,
  type CatalogPickerProduct,
} from '@/components/products/catalog-product-picker'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatMoney, formatNumber, formatStatus } from '@/lib/utils'
import { getApiErrorMessage } from '@/lib/api-error'

type RegionCode = 'US_CONTIGUOUS' | 'CANADA' | 'MEXICO'

type RegionForm = {
  enabled: boolean
  currency: string
  markupPercent: string
}

type PolicyForm = {
  markupPercent: string
  manufacturerBookingMarkupPercent: string
  inferencePercentile: number
  fallbackPercentile: number
  freshCacheMinutes: string
  staleCacheHours: string
  leadTimeDays: string
  consolidationDelayDays: string
  regions: Record<RegionCode, RegionForm>
}

type SimulatorLine = {
  key: string
  product_uuid: string
  product_id: string
  variant_id: string
  quantity: string
  price: string
  name: string
  sku: string
  manufacturer_name: string
  packing: {
    mode: 'catalog' | 'auto' | 'parcel' | 'ltl'
    packages: string | null
    stackable: 'catalog' | 'yes' | 'no'
    length: string | null
    width: string | null
    height: string | null
    weight: string | null
    freightClass: string | null
  }
}

type ProductShippingFacts = components['schemas']['ProductShippingFactsRead']

export function QuoteAttemptLink({
  attemptId,
  children,
  className,
}: {
  attemptId: number
  children: ReactNode
  className?: string
}) {
  return (
    <Link
      to="/shipping-calculator/quote-attempts/$attemptId"
      params={{ attemptId: String(attemptId) }}
      className={className}
    >
      {children}
    </Link>
  )
}

function numericShopifyId(value?: string | null) {
  if (!value) return null
  const id = Number(value.split('/').at(-1))
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

type DecisionComponent = {
  group_key?: string
  mode?: string | null
  source?: string
  base_cost?: string | number
  markup_percent?: string | number
  handling_fee?: string | number
  markup_cap?: string | number | null
  customer_cost?: string | number
  carrier_name?: string | null
  service_name?: string | null
  quote_id?: string | null
  transit_days?: number | null
  physical_package_count?: number
  unit_kind?: string
  unit_count?: number
  item_count?: number
  uses_estimated_dimensions?: boolean
  detail?: string
  warnings?: string[]
}

type SimulationDecision = {
  policy_version?: number
  result_source?: string
  discounted_subtotal?: string | number
  warnings?: string[]
  options?: Array<{
    service_name?: string
    total?: string | number
    description?: string
    min_delivery_date?: string | null
    max_delivery_date?: string | null
    components?: DecisionComponent[]
  }>
  groups?: Array<{
    key?: string
    manufacturer_name?: string
    ready_date?: string
    origin?: { name?: string; postal_code?: string; confidence?: string }
    candidate_plans?: Array<{
      mode?: string
      total_weight_lbs?: string | number
      handling_unit_count?: number
      estimated_pallet_count?: number
      warnings?: string[]
      packages?: Array<{
        product_id?: number | null
        title?: string
        quantity?: number
        length_in?: string | number
        width_in?: string | number
        height_in?: string | number
        weight_lbs?: string | number
        freight_class?: string
        stackable?: boolean
      }>
    }>
    items?: Array<{
      product_id?: number | null
      title?: string
      sku?: string | null
      quantity?: number
      profile?: {
        mode?: string
        classification_mode?: string
        classification_reason?: string
        packages_per_unit?: number
        stackable?: boolean
        length_in?: string | number
        width_in?: string | number
        height_in?: string | number
        weight_lbs?: string | number
        freight_class?: string
        confidence?: string
        provenance?: Record<string, unknown>
      }
    }>
    rating_candidates?: Array<{
      mode?: string | null
      source?: string
      base_cost?: string | number
      markup_percent?: string | number
      handling_fee?: string | number
      markup_cap?: string | number | null
      customer_cost?: string | number
      carrier_name?: string | null
      service_name?: string | null
      quote_id?: string | null
      transit_days?: number | null
      selected?: boolean
      warnings?: string[]
    }>
  }>
  arrangement_decisions?: Array<{
    dock_key?: string
    selected_strategy?: string
    alternatives?: Array<{
      strategy?: string
      shipment_count?: number
      base_cost?: string | number
      customer_cost?: string | number
      selected?: boolean
    }>
  }>
}

function policyForm(policy: ShippingPolicy): PolicyForm {
  const configs = (policy.region_configs ?? {}) as Record<string, Record<string, unknown>>
  const region = (code: RegionCode, currency: string): RegionForm => ({
    enabled: Boolean(configs[code]?.enabled ?? code === 'US_CONTIGUOUS'),
    currency: String(configs[code]?.currency ?? currency),
    markupPercent: String(configs[code]?.carrier_markup_percent ?? policy.carrier_markup_percent),
  })
  return {
    markupPercent: String(policy.carrier_markup_percent),
    manufacturerBookingMarkupPercent: String(Number(policy.manufacturer_booking_markup_percent)),
    inferencePercentile: policy.product_inference_percentile,
    fallbackPercentile: policy.fallback_percentile,
    freshCacheMinutes: String(policy.fresh_cache_seconds / 60),
    staleCacheHours: String(policy.stale_cache_seconds / 3600),
    leadTimeDays: String(policy.default_lead_time_days),
    consolidationDelayDays: String(policy.max_consolidation_delay_days),
    regions: {
      US_CONTIGUOUS: region('US_CONTIGUOUS', 'USD'),
      CANADA: region('CANADA', 'CAD'),
      MEXICO: region('MEXICO', 'MXN'),
    },
  }
}

// A draft percent setting's read view; an empty box reads "Not set".
function percentText(value: string) {
  return value === '' ? undefined : `${value}%`
}

function errorMessage(error: unknown): string {
  return getApiErrorMessage(error, 'The operation failed')
}

function statusBadge(status: string) {
  return (
    <Badge
      variant={
        status === 'published' || status === 'complete'
          ? 'default'
          : status === 'failed'
            ? 'destructive'
            : 'secondary'
      }
    >
      {formatStatus(status)}
    </Badge>
  )
}

function MetricCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="px-4 text-xs text-muted-foreground">{detail}</CardContent>
    </Card>
  )
}

function PercentileControl({
  label,
  value,
  onChange,
  description,
  disabled,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  description: string
  disabled?: boolean
}) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Label>{label}</Label>
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold">P</span>
          <POInlineEditableField
            kind="number"
            ariaLabel={label}
            value={value}
            disabled={disabled}
            buttonClassName="min-w-16 justify-center text-lg font-semibold"
            inputClassName="w-20 text-center"
            onConfirm={(raw) => onChange(Math.min(99, Math.max(50, Number(raw))))}
          />
        </div>
      </div>
      <input
        className="mt-4 w-full accent-primary"
        type="range"
        min={50}
        max={99}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span>Less conservative</span>
        <span>More conservative</span>
      </div>
    </div>
  )
}

export function PolicyTab({ selectedPolicy }: { selectedPolicy?: ShippingPolicy }) {
  const [form, setForm] = useState<PolicyForm | null>(() =>
    selectedPolicy ? policyForm(selectedPolicy) : null,
  )
  const readiness = useShippingReadiness(selectedPolicy?.id)
  const createDraft = useCreateShippingPolicyDraft()
  const updatePolicy = useUpdateShippingPolicy()
  const rebuildEstimates = useRebuildShippingEstimates()
  const publishPolicy = usePublishShippingPolicy()
  const rollbackPolicy = useRollbackShippingPolicy()

  if (!selectedPolicy || !form) {
    return (
      <div className="rounded-lg border p-6 text-sm text-muted-foreground">No policy found.</div>
    )
  }
  const isDraft = selectedPolicy.status === 'draft'
  const snapshotProgress =
    readiness.data?.estimate_snapshot_progress ??
    (readiness.data?.active_shopify_products
      ? (readiness.data.estimate_snapshot_products / readiness.data.active_shopify_products) * 100
      : 0)
  const snapshotTaskRunning = ['pending', 'in-progress'].includes(
    readiness.data?.estimate_snapshot_task_status ?? '',
  )

  const save = async () => {
    if (!selectedPolicy.id) return
    const body: ShippingPolicyUpdate = {
      carrier_markup_percent: form.markupPercent,
      manufacturer_booking_markup_percent: form.manufacturerBookingMarkupPercent,
      product_inference_percentile: form.inferencePercentile,
      fallback_percentile: form.fallbackPercentile,
      fresh_cache_seconds: Math.round(Number(form.freshCacheMinutes) * 60),
      stale_cache_seconds: Math.round(Number(form.staleCacheHours) * 3600),
      default_lead_time_days: Number(form.leadTimeDays),
      max_consolidation_delay_days: Number(form.consolidationDelayDays),
      region_configs: Object.fromEntries(
        (Object.entries(form.regions) as Array<[RegionCode, RegionForm]>).map(([code, config]) => [
          code,
          {
            enabled: config.enabled,
            currency: config.currency.toUpperCase(),
            carrier_markup_percent: config.markupPercent,
          },
        ]),
      ),
    }
    try {
      await updatePolicy.mutateAsync({ policyId: selectedPolicy.id, body })
      toast.success('Draft policy saved')
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard
          label="Policy"
          value={`v${selectedPolicy.version}`}
          detail={`${formatStatus(selectedPolicy.status)} · ${selectedPolicy.region_code}`}
        />
        <MetricCard
          label="Free freight"
          value="Manufacturer rules"
          detail="No cart-wide threshold"
        />
        <MetricCard
          label="Carrier pricing"
          value={`×${(1 + Number(selectedPolicy.carrier_markup_percent) / 100).toFixed(2)}`}
          detail={`${selectedPolicy.carrier_markup_percent}% true markup`}
        />
        <MetricCard
          label="Activation readiness"
          value={readiness.data?.ready_to_publish ? 'Ready' : 'Blocked'}
          detail={`${readiness.data?.blockers.length ?? 0} blocking checks`}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5" /> Policy settings
                </CardTitle>
                <CardDescription>
                  Money-affecting settings publish atomically. Canada and Mexico remain modeled but
                  disabled.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {statusBadge(selectedPolicy.status)}
                {!isDraft && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={createDraft.isPending}
                    onClick={async () => {
                      try {
                        await createDraft.mutateAsync({
                          sourcePolicyId: selectedPolicy.id ?? undefined,
                        })
                        toast.success('New draft created')
                      } catch (error) {
                        toast.error(errorMessage(error))
                      }
                    }}
                  >
                    <Plus /> Create draft
                  </Button>
                )}
                {selectedPolicy.status === 'archived' && selectedPolicy.id != null && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!readiness.data?.ready_to_publish || rollbackPolicy.isPending}
                    onClick={async () => {
                      try {
                        await rollbackPolicy.mutateAsync(selectedPolicy.id as number)
                        toast.success(`Policy v${selectedPolicy.version} restored`)
                      } catch (error) {
                        toast.error(errorMessage(error))
                      }
                    }}
                  >
                    <RotateCcw /> Restore
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>True carrier markup (%)</Label>
                <POInlineEditableField
                  kind="number"
                  ariaLabel="True carrier markup (%)"
                  disabled={!isDraft}
                  value={form.markupPercent}
                  displayValue={percentText(form.markupPercent)}
                  placeholder="Not set"
                  buttonClassName="-ml-2"
                  onConfirm={(raw) =>
                    setForm({
                      ...form,
                      markupPercent: raw,
                      regions: {
                        ...form.regions,
                        US_CONTIGUOUS: {
                          ...form.regions.US_CONTIGUOUS,
                          markupPercent: raw,
                        },
                      },
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  A $100 carrier cost becomes{' '}
                  {formatMoney(100 * (1 + Number(form.markupPercent) / 100))}.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Manufacturer-booked markup (%)</Label>
                <POInlineEditableField
                  kind="number"
                  ariaLabel="Manufacturer-booked markup (%)"
                  disabled={!isDraft}
                  value={form.manufacturerBookingMarkupPercent}
                  displayValue={percentText(form.manufacturerBookingMarkupPercent)}
                  placeholder="Not set"
                  buttonClassName="-ml-2"
                  onConfirm={(raw) => setForm({ ...form, manufacturerBookingMarkupPercent: raw })}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label>Region configurations</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Canada and Mexico are fully versioned options, but v1 readiness deliberately
                  blocks publishing them enabled.
                </p>
              </div>
              <div className="grid gap-3 lg:grid-cols-3">
                {(
                  [
                    ['US_CONTIGUOUS', 'Contiguous US + DC'],
                    ['CANADA', 'Canada (future)'],
                    ['MEXICO', 'Mexico (future)'],
                  ] as const
                ).map(([code, label]) => {
                  const config = form.regions[code]
                  const updateRegion = (change: Partial<RegionForm>) => {
                    const synchronized =
                      code === 'US_CONTIGUOUS'
                        ? {
                            markupPercent: change.markupPercent ?? form.markupPercent,
                          }
                        : {}
                    setForm({
                      ...form,
                      ...synchronized,
                      regions: {
                        ...form.regions,
                        [code]: { ...config, ...change },
                      },
                    })
                  }
                  return (
                    <div key={code} className="space-y-3 rounded-lg border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium">{label}</p>
                          <p className="font-mono text-[11px] text-muted-foreground">{code}</p>
                        </div>
                        <Switch
                          checked={config.enabled}
                          disabled={!isDraft}
                          onCheckedChange={(enabled) => updateRegion({ enabled })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div>
                          <p>Currency</p>
                          <POInlineEditableField
                            ariaLabel={`${label} currency`}
                            disabled={!isDraft}
                            value={config.currency}
                            placeholder="Not set"
                            buttonClassName="-ml-2 min-w-0 text-foreground"
                            onConfirm={(raw) =>
                              updateRegion({
                                // The currency is a three-letter code.
                                currency: raw.toUpperCase().slice(0, 3),
                              })
                            }
                          />
                        </div>
                        <div>
                          <p>Markup</p>
                          <POInlineEditableField
                            kind="number"
                            ariaLabel={`${label} markup`}
                            disabled={!isDraft}
                            value={config.markupPercent}
                            displayValue={percentText(config.markupPercent)}
                            placeholder="Not set"
                            buttonClassName="-ml-2 min-w-0 text-foreground"
                            onConfirm={(raw) => updateRegion({ markupPercent: raw })}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <PercentileControl
                label="Product-data inference"
                value={form.inferencePercentile}
                onChange={(value) => isDraft && setForm({ ...form, inferencePercentile: value })}
                disabled={!isDraft}
                description="Applied to precomputed package dimensions and weights. Saving a change requires a complete new snapshot."
              />
              <PercentileControl
                label="Fallback carrier estimate"
                value={form.fallbackPercentile}
                onChange={(value) => isDraft && setForm({ ...form, fallbackPercentile: value })}
                disabled={!isDraft}
                description="Independent percentile for the WWEX-unavailable rate card. Publication requires matching holdout coverage."
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {(
                [
                  ['freshCacheMinutes', 'Fresh cache (minutes)', 'min'],
                  ['staleCacheHours', 'Stale cache (hours)', 'h'],
                  ['leadTimeDays', 'Default lead time', 'days'],
                  ['consolidationDelayDays', 'Max consolidation delay', 'days'],
                ] as const
              ).map(([field, label, unit]) => (
                <div key={field} className="space-y-2">
                  <Label>{label}</Label>
                  <POInlineEditableField
                    kind="number"
                    ariaLabel={label}
                    disabled={!isDraft}
                    value={form[field]}
                    displayValue={form[field] === '' ? undefined : `${form[field]} ${unit}`}
                    placeholder="Not set"
                    buttonClassName="-ml-2"
                    onConfirm={(raw) => setForm({ ...form, [field]: raw })}
                  />
                </div>
              ))}
            </div>

            {isDraft && (
              <div className="flex flex-wrap gap-2 border-t pt-4">
                <Button onClick={save} disabled={updatePolicy.isPending}>
                  <Save /> Save draft
                </Button>
                <Button
                  variant="outline"
                  disabled={rebuildEstimates.isPending || snapshotTaskRunning}
                  onClick={async () => {
                    if (!selectedPolicy.id) return
                    try {
                      const queued = await rebuildEstimates.mutateAsync(selectedPolicy.id)
                      toast.success(
                        `Estimate snapshot queued · task ${queued.task_uuid.slice(0, 8)}`,
                      )
                    } catch (error) {
                      toast.error(errorMessage(error))
                    }
                  }}
                >
                  <RefreshCw className={rebuildEstimates.isPending ? 'animate-spin' : ''} /> Rebuild
                  P{form.inferencePercentile} snapshot
                </Button>
                <Button
                  variant="secondary"
                  disabled={!readiness.data?.ready_to_publish || publishPolicy.isPending}
                  onClick={async () => {
                    if (!selectedPolicy.id) return
                    try {
                      await publishPolicy.mutateAsync(selectedPolicy.id)
                      toast.success(`Policy v${selectedPolicy.version} published`)
                    } catch (error) {
                      toast.error(errorMessage(error))
                    }
                  }}
                >
                  <ShieldCheck /> Publish
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Readiness gates</CardTitle>
            <CardDescription>Every item must pass before the draft can publish.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="mb-2 flex justify-between text-sm">
                <span>Product snapshot</span>
                <span>
                  {formatNumber(readiness.data?.estimate_snapshot_products)} /{' '}
                  {formatNumber(readiness.data?.active_shopify_products)}
                </span>
              </div>
              <Progress value={snapshotProgress} />
              <p className="mt-2 text-xs text-muted-foreground">
                {formatStatus(readiness.data?.estimate_snapshot_status ?? 'pending')}
                {readiness.data?.estimate_snapshot_task_status
                  ? ` · worker ${formatStatus(readiness.data.estimate_snapshot_task_status)}`
                  : ''}
                {readiness.data?.estimate_snapshot_task_uuid
                  ? ` · ${readiness.data.estimate_snapshot_task_uuid.slice(0, 8)}`
                  : ''}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground">Active origins</p>
                <p className="mt-1 text-xl font-semibold">
                  {formatNumber(readiness.data?.active_origins)}
                </p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground">Missing origins</p>
                <p className="mt-1 text-xl font-semibold">
                  {formatNumber(readiness.data?.estimates_missing_origin)}
                </p>
              </div>
            </div>
            {readiness.data?.ready_to_publish ? (
              <div className="flex gap-2 rounded-md border border-green-500/30 bg-green-500/10 p-3 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" /> All publication gates
                pass.
              </div>
            ) : (
              <div className="space-y-2">
                {(readiness.data?.blockers ?? []).map((blocker) => (
                  <div
                    key={blocker}
                    className="flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    {blocker}
                  </div>
                ))}
              </div>
            )}
            {readiness.data?.estimate_snapshot_error && (
              <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
                {readiness.data.estimate_snapshot_error}
              </div>
            )}
            {(readiness.data?.invalid_freight_rules ?? []).length > 0 && (
              <div className="rounded-md border p-3 text-xs">
                <p className="font-medium">Freight rules requiring action</p>
                <div className="mt-2 space-y-2 text-muted-foreground">
                  {readiness.data?.invalid_freight_rules.map((issue) => (
                    <p key={issue.rule_id}>
                      <span className="font-medium text-foreground">{issue.manufacturer_name}</span>
                      {' · '}
                      {issue.reasons.join('; ')}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {readiness.data?.matching_rate_card && (
              <div className="rounded-md border p-3 text-xs">
                <p className="font-medium">
                  P{readiness.data.matching_rate_card.percentile} fallback calibration
                </p>
                <p className="mt-1 text-muted-foreground">
                  {formatNumber(readiness.data.matching_rate_card.observation_count)} observations ·{' '}
                  {formatNumber(readiness.data.matching_rate_card.holdout_observations)} /{' '}
                  {formatNumber(readiness.data.matching_rate_card.required_holdout_observations)}{' '}
                  holdout ·{' '}
                  {(Number(readiness.data.matching_rate_card.holdout_coverage) * 100).toFixed(1)}% /{' '}
                  {(Number(readiness.data.matching_rate_card.required_coverage) * 100).toFixed(0)}%
                  coverage
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

const EMPTY_PACKING: SimulatorLine['packing'] = {
  mode: 'catalog',
  packages: null,
  stackable: 'catalog',
  length: null,
  width: null,
  height: null,
  weight: null,
  freightClass: null,
}

function dimensionText(
  length?: string | number | null,
  width?: string | number | null,
  height?: string | number | null,
) {
  if (length == null || width == null || height == null) return 'Dimensions loading'
  return `${formatNumber(length)} × ${formatNumber(width)} × ${formatNumber(height)} in`
}

function inputNumber(value?: string | number | null) {
  if (value == null || value === '') return ''
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return String(value)
  return String(Math.round(numeric * 100) / 100)
}

function ProductSimulatorLine({
  line,
  policyId,
  onChange,
  onRemove,
}: {
  line: SimulatorLine
  policyId?: number | null
  onChange: (line: SimulatorLine) => void
  onRemove: () => void
}) {
  const shippingFacts = useProductShippingFacts(line.product_uuid, true, policyId)
  const facts = shippingFacts.data as ProductShippingFacts | undefined
  const packing = line.packing
  const hasOverride =
    packing.mode !== 'catalog' ||
    packing.stackable !== 'catalog' ||
    Boolean(
      packing.packages ||
      packing.length ||
      packing.width ||
      packing.height ||
      packing.weight ||
      packing.freightClass,
    )
  const effectiveMode = packing.mode === 'catalog' ? facts?.classification_mode : packing.mode
  const effectivePackages = packing.packages || facts?.packages_per_unit
  const effectiveLength = packing.length || facts?.length_in
  const effectiveWidth = packing.width || facts?.width_in
  const effectiveHeight = packing.height || facts?.height_in
  const effectiveWeight = packing.weight || facts?.weight_lbs
  const updatePacking = (change: Partial<SimulatorLine['packing']>) =>
    onChange({ ...line, packing: { ...packing, ...change } })

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 p-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
          <Box className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium leading-snug">{line.name}</p>
          <p className="mt-0.5 flex flex-wrap gap-x-1 text-xs text-muted-foreground">
            <span>{line.manufacturer_name}</span>
            <span>·</span>
            <span className="break-all">{line.sku}</span>
          </p>
        </div>
        <Button type="button" size="xs" variant="ghost" onClick={onRemove}>
          Remove
        </Button>
      </div>

      <div className="grid gap-3 border-t bg-muted/20 p-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`quantity-${line.key}`}>Quantity</Label>
          <InlineFieldInput
            id={`quantity-${line.key}`}
            type="number"
            min={1}
            value={line.quantity}
            onChange={(event) => onChange({ ...line, quantity: event.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`price-${line.key}`}>Unit price</Label>
          <InlineFieldInput
            id={`price-${line.key}`}
            type="number"
            min={0}
            step="0.01"
            value={line.price}
            onChange={(event) => onChange({ ...line, price: event.target.value })}
          />
        </div>
      </div>

      <details className="group border-t">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-sm outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
          <span className="flex shrink-0 items-center gap-2 font-medium">
            Packing
            {hasOverride ? <Badge variant="secondary">Quote-only override</Badge> : null}
          </span>
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {shippingFacts.isLoading
              ? 'Loading catalog facts…'
              : `${formatStatus(effectiveMode)} · ${effectivePackages ?? '—'} per unit · ${dimensionText(effectiveLength, effectiveWidth, effectiveHeight)} · ${formatNumber(effectiveWeight)} lb`}
          </span>
        </summary>
        <div className="space-y-4 border-t p-3">
          {shippingFacts.isError ? (
            <div className="flex gap-2 rounded-md bg-destructive/10 p-3 text-xs text-destructive">
              <AlertTriangle className="size-4 shrink-0" /> Shipping facts could not be loaded.
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {facts?.origin_name ?? 'Origin loading'}
              {facts?.origin_postal_code ? ` · ${facts.origin_postal_code}` : ''}
              {facts?.confidence ? ` · ${formatStatus(facts.confidence)} confidence` : ''}
            </span>
            {hasOverride ? (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                onClick={() => onChange({ ...line, packing: { ...EMPTY_PACKING } })}
              >
                <RotateCcw /> Reset to catalog
              </Button>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Shipping mode</Label>
              <Select
                value={packing.mode}
                onValueChange={(mode) =>
                  updatePacking({
                    mode: mode as SimulatorLine['packing']['mode'],
                  })
                }
              >
                <SelectTrigger aria-label="Shipping mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="catalog">
                    Catalog ({formatStatus(facts?.classification_mode)})
                  </SelectItem>
                  <SelectItem value="auto">Recalculate automatically</SelectItem>
                  <SelectItem value="parcel">Force parcel</SelectItem>
                  <SelectItem value="ltl">Force freight</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Stacking</Label>
              <Select
                value={packing.stackable}
                onValueChange={(stackable) =>
                  updatePacking({
                    stackable: stackable as SimulatorLine['packing']['stackable'],
                  })
                }
              >
                <SelectTrigger aria-label="Stacking">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="catalog">
                    Catalog ({facts?.stackable ? 'stackable' : 'not stackable'})
                  </SelectItem>
                  <SelectItem value="yes">Stackable</SelectItem>
                  <SelectItem value="no">Not stackable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`packages-${line.key}`}>Packages per unit</Label>
              <InlineFieldInput
                id={`packages-${line.key}`}
                type="number"
                min={1}
                value={packing.packages ?? String(facts?.packages_per_unit ?? '')}
                onChange={(event) => updatePacking({ packages: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`weight-${line.key}`}>Weight per package (lb)</Label>
              <InlineFieldInput
                id={`weight-${line.key}`}
                type="number"
                min={0.01}
                step="0.01"
                value={packing.weight ?? inputNumber(facts?.weight_lbs)}
                onChange={(event) => updatePacking({ weight: event.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ['length', 'Length', facts?.length_in],
                ['width', 'Width', facts?.width_in],
                ['height', 'Height', facts?.height_in],
              ] as const
            ).map(([field, label, catalogValue]) => (
              <div key={field} className="space-y-1.5">
                <Label htmlFor={`${field}-${line.key}`}>{label} (in)</Label>
                <InlineFieldInput
                  id={`${field}-${line.key}`}
                  type="number"
                  min={0.01}
                  step="0.01"
                  value={packing[field] ?? inputNumber(catalogValue)}
                  onChange={(event) => updatePacking({ [field]: event.target.value })}
                />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label htmlFor={`freight-class-${line.key}`}>Freight class</Label>
              <InlineFieldInput
                id={`freight-class-${line.key}`}
                inputMode="decimal"
                value={packing.freightClass ?? String(facts?.freight_class ?? '')}
                onChange={(event) => updatePacking({ freightClass: event.target.value })}
              />
            </div>
          </div>
        </div>
      </details>
    </div>
  )
}

function componentMarkup(component: DecisionComponent) {
  return Math.max(
    0,
    Number(component.customer_cost ?? 0) -
      Number(component.base_cost ?? 0) -
      Number(component.handling_fee ?? 0),
  )
}

export function SimulatorTab({ policy }: { policy?: ShippingPolicy }) {
  const simulate = useSimulateShippingQuote()
  const [destinationZip, setDestinationZip] = useState('75201')
  const [lines, setLines] = useState<SimulatorLine[]>([])
  const [lastCalculatedSignature, setLastCalculatedSignature] = useState<string | null>(null)
  const decision = simulate.data as SimulationDecision | undefined
  const option = decision?.options?.[0]
  const inputSignature = useMemo(
    () =>
      JSON.stringify({
        destinationZip,
        policyId: policy?.id,
        policyUpdatedAt: policy?.updated_at,
        lines,
      }),
    [destinationZip, lines, policy?.id, policy?.updated_at],
  )
  const resultIsStale = Boolean(option && lastCalculatedSignature !== inputSignature)

  const addProduct = (product: CatalogPickerProduct) => {
    const productId = numericShopifyId(product.shopify_product_id)
    const variantId = numericShopifyId(product.shopify_variant_id)
    const storePrice = product.current_price?.store_price
    if (!productId || !variantId || !storePrice) {
      toast.error('This product is missing a Shopify ID or current price')
      return
    }
    setLines((current) => {
      const existing = current.find((line) => line.product_uuid === product.uuid)
      if (existing) {
        return current.map((line) =>
          line.key === existing.key
            ? { ...line, quantity: String(Number(line.quantity || 0) + 1) }
            : line,
        )
      }
      return [
        ...current,
        {
          key: product.uuid,
          product_uuid: product.uuid,
          product_id: String(productId),
          variant_id: String(variantId),
          quantity: '1',
          price: Number(storePrice).toFixed(2),
          name: product.store_title || product.model,
          sku: product.model,
          manufacturer_name: product.manufacturer_name,
          packing: { ...EMPTY_PACKING },
        },
      ]
    })
  }

  const run = () => {
    const request: ShippingSimulatorRequest = {
      destination_postal_code: destinationZip,
      currency: 'USD',
      policy_id: policy?.id,
      lines: lines.map((line) => {
        const packing = line.packing
        const packingOverride = {
          ...(packing.mode !== 'catalog' ? { mode_override: packing.mode } : {}),
          ...(packing.packages ? { packages_per_unit: Number(packing.packages) } : {}),
          ...(packing.stackable !== 'catalog' ? { stackable: packing.stackable === 'yes' } : {}),
          ...(packing.length ? { length_in: packing.length } : {}),
          ...(packing.width ? { width_in: packing.width } : {}),
          ...(packing.height ? { height_in: packing.height } : {}),
          ...(packing.weight ? { weight_lbs: packing.weight } : {}),
          ...(packing.freightClass ? { freight_class: packing.freightClass } : {}),
        }
        return {
          product_id: Number(line.product_id),
          variant_id: Number(line.variant_id),
          quantity: Number(line.quantity),
          price_cents: Math.round(Number(line.price) * 100),
          grams: 0,
          name: line.name,
          sku: line.sku,
          requires_shipping: true,
          ...(Object.keys(packingOverride).length ? { packing_override: packingOverride } : {}),
        }
      }),
    }
    const submittedSignature = inputSignature
    simulate.mutate(request, {
      onSuccess: () => setLastCalculatedSignature(submittedSignature),
      onError: (error) => toast.error(errorMessage(error)),
    })
  }

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] items-start gap-6 xl:grid-cols-[minmax(24rem,0.8fr)_minmax(0,1.35fr)]">
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageSearch className="h-5 w-5" /> Build a test cart
          </CardTitle>
          <CardDescription>
            Search the catalog, then adjust packing for this quote only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="max-w-xs space-y-2">
            <Label htmlFor="simulator-destination-zip">Destination ZIP</Label>
            <InlineFieldInput
              id="simulator-destination-zip"
              inputMode="numeric"
              autoComplete="postal-code"
              value={destinationZip}
              onChange={(event) => setDestinationZip(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              City and state are resolved automatically for the live provider quote.
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label>Products</Label>
              {lines.length ? (
                <span className="text-xs text-muted-foreground">
                  {lines.length} product{lines.length === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
            {lines.map((line) => (
              <ProductSimulatorLine
                key={line.key}
                line={line}
                policyId={policy?.id}
                onChange={(nextLine) =>
                  setLines((current) =>
                    current.map((currentLine) =>
                      currentLine.key === nextLine.key ? nextLine : currentLine,
                    ),
                  )
                }
                onRemove={() =>
                  setLines((current) =>
                    current.filter((currentLine) => currentLine.key !== line.key),
                  )
                }
              />
            ))}
            <CatalogProductPicker onSelect={addProduct} disabled={simulate.isPending} />
          </div>

          <LoadingButton
            className="w-full"
            loading={simulate.isPending}
            loadingText="Calculating..."
            disabled={
              !lines.length ||
              destinationZip.trim().length < 3 ||
              lines.some((line) => Number(line.quantity) < 1 || Number(line.price) < 0)
            }
            onClick={run}
          >
            <Play /> Calculate freight estimate
          </LoadingButton>
        </CardContent>
      </Card>

      <div className="min-w-0 space-y-6">
        {!option ? (
          <Card>
            <CardContent className="flex min-h-[28rem] flex-col items-center justify-center px-6 text-center text-muted-foreground">
              <Truck className="mb-3 h-10 w-10" />
              <p className="font-medium text-foreground">
                Add a product to build a freight estimate
              </p>
              <p className="mt-1 max-w-md text-sm">
                The result will show customer price, provider rates, fees, packing plans, and
                shipment decisions.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {resultIsStale ? (
              <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-700" />
                Inputs changed. Recalculate to refresh this estimate.
              </div>
            ) : null}
            {(decision.warnings?.length ?? 0) > 0 ? (
              <div className="space-y-2">
                {decision.warnings?.map((warning) => (
                  <div
                    key={warning}
                    className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm"
                  >
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-700" />
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            ) : null}
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardDescription>Customer rate</CardDescription>
                    <CardTitle className="mt-1 text-2xl">{option.service_name}</CardTitle>
                    <p className="mt-2 text-sm text-muted-foreground">{option.description}</p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-3xl font-semibold tabular-nums">
                      {formatMoney(option.total)}
                    </p>
                    <Badge variant="outline">{formatStatus(decision?.result_source)}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid gap-3 border-t pt-4 text-sm sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Delivery</p>
                  <p className="mt-1 font-medium">
                    {option.min_delivery_date ?? '—'} to {option.max_delivery_date ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Merchandise subtotal</p>
                  <p className="mt-1 font-medium tabular-nums">
                    {formatMoney(decision?.discounted_subtotal)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Policy</p>
                  <p className="mt-1 font-medium">
                    v{decision?.policy_version ?? policy?.version ?? '—'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer price breakdown</CardTitle>
                <CardDescription>
                  Provider cost, markup, fees, and final charge by shipment.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto px-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">Shipment</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">Markup</TableHead>
                      <TableHead className="text-right">Fee</TableHead>
                      <TableHead className="pr-6 text-right">Customer</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(option.components ?? []).map((component, index) => (
                      <TableRow key={`${component.group_key}-${index}`}>
                        <TableCell className="pl-6">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{formatStatus(component.mode)}</Badge>
                            <Badge variant="outline">{formatStatus(component.source)}</Badge>
                          </div>
                          <p className="mt-1 max-w-64 text-xs text-muted-foreground">
                            {component.unit_count ?? component.physical_package_count ?? 1}{' '}
                            {formatStatus(component.unit_kind ?? 'package')}
                            {(component.unit_count ?? 1) === 1 ? '' : 's'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{component.carrier_name || 'Internal rate'}</p>
                          <p className="text-xs text-muted-foreground">
                            {component.service_name || component.detail}
                          </p>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(component.base_cost)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <p>{formatMoney(componentMarkup(component))}</p>
                          <p className="text-xs text-muted-foreground">
                            {component.markup_percent ?? 0}%
                          </p>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(component.handling_fee)}
                        </TableCell>
                        <TableCell className="pr-6 text-right font-semibold tabular-nums">
                          {formatMoney(component.customer_cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Shipment plan and provider candidates</CardTitle>
                <CardDescription>
                  Resolved product facts and every rated plan considered.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {(decision.groups ?? []).map((group) => (
                  <section
                    key={group.key}
                    className="space-y-4 border-b pb-5 last:border-b-0 last:pb-0"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{group.manufacturer_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {group.origin?.name} · {group.origin?.postal_code} · ready{' '}
                          {group.ready_date}
                        </p>
                      </div>
                      <Badge
                        variant={group.origin?.confidence === 'low' ? 'destructive' : 'outline'}
                      >
                        {formatStatus(group.origin?.confidence)} origin
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      {(group.items ?? []).map((item, itemIndex) => (
                        <div
                          key={`${item.product_id}-${itemIndex}`}
                          className="grid gap-2 rounded-md bg-muted/40 p-3 text-sm sm:grid-cols-[minmax(0,1fr)_auto]"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">{item.title}</p>
                            <p className="text-xs text-muted-foreground">
                              Qty {item.quantity} ·{' '}
                              {formatStatus(item.profile?.classification_mode)} ·{' '}
                              {item.profile?.packages_per_unit} per unit
                            </p>
                          </div>
                          <div className="text-xs text-muted-foreground sm:text-right">
                            <p>
                              {dimensionText(
                                item.profile?.length_in,
                                item.profile?.width_in,
                                item.profile?.height_in,
                              )}
                            </p>
                            <p>
                              {formatNumber(item.profile?.weight_lbs)} lb · class{' '}
                              {item.profile?.freight_class ?? '—'} ·{' '}
                              {item.profile?.stackable ? 'stackable' : 'not stackable'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2">
                      {(group.candidate_plans ?? []).map((plan, planIndex) => (
                        <div key={`${plan.mode}-${planIndex}`} className="rounded-md border p-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium">{formatStatus(plan.mode)} plan</p>
                            <Badge variant="secondary">
                              {formatNumber(plan.total_weight_lbs)} lb
                            </Badge>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {plan.handling_unit_count} handling unit
                            {plan.handling_unit_count === 1 ? '' : 's'}
                            {plan.estimated_pallet_count
                              ? ` · ${plan.estimated_pallet_count} pallet${plan.estimated_pallet_count === 1 ? '' : 's'}`
                              : ''}
                          </p>
                          {(plan.packages ?? []).map((pkg, packageIndex) => (
                            <p
                              key={`${pkg.product_id}-${packageIndex}`}
                              className="mt-2 border-t pt-2 text-xs text-muted-foreground"
                            >
                              {pkg.quantity} ×{' '}
                              {dimensionText(pkg.length_in, pkg.width_in, pkg.height_in)} ·{' '}
                              {formatNumber(pkg.weight_lbs)} lb · class {pkg.freight_class}
                            </p>
                          ))}
                        </div>
                      ))}
                    </div>

                    {(group.rating_candidates?.length ?? 0) > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Candidate</TableHead>
                              <TableHead>Provider</TableHead>
                              <TableHead className="text-right">Base</TableHead>
                              <TableHead className="text-right">Markup + fee</TableHead>
                              <TableHead className="text-right">Customer</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.rating_candidates?.map((candidate, candidateIndex) => (
                              <TableRow
                                key={`${candidate.mode}-${candidate.source}-${candidateIndex}`}
                                className={candidate.selected ? 'bg-muted/60' : undefined}
                              >
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {formatStatus(candidate.mode)}
                                    </span>
                                    {candidate.selected ? <Badge>Selected</Badge> : null}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {formatStatus(candidate.source)}
                                    {candidate.transit_days != null
                                      ? ` · ${candidate.transit_days} transit days`
                                      : ''}
                                  </p>
                                </TableCell>
                                <TableCell>
                                  <p>{candidate.carrier_name || 'Internal rate'}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {candidate.service_name || '—'}
                                  </p>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {formatMoney(candidate.base_cost)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {candidate.markup_percent ?? 0}% +{' '}
                                  {formatMoney(candidate.handling_fee)}
                                </TableCell>
                                <TableCell className="text-right font-medium tabular-nums">
                                  {formatMoney(candidate.customer_cost)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : null}
                  </section>
                ))}
              </CardContent>
            </Card>

            {(decision.arrangement_decisions?.length ?? 0) > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>Split and consolidation comparison</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {decision.arrangement_decisions?.map((arrangement, index) => (
                    <div
                      key={`${arrangement.dock_key}-${index}`}
                      className="border-b pb-3 last:border-0 last:pb-0"
                    >
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-sm font-medium">Dock {arrangement.dock_key}</p>
                        <Badge>{formatStatus(arrangement.selected_strategy)}</Badge>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {arrangement.alternatives?.map((alternative, alternativeIndex) => (
                          <div
                            key={`${alternative.strategy}-${alternativeIndex}`}
                            className={`rounded-md border p-3 text-sm ${alternative.selected ? 'bg-muted/60' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">
                                {formatStatus(alternative.strategy)}
                              </span>
                              {alternative.selected ? (
                                <Badge variant="outline">Selected</Badge>
                              ) : null}
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {alternative.shipment_count} shipment
                              {alternative.shipment_count === 1 ? '' : 's'} ·{' '}
                              {formatMoney(alternative.base_cost)} base ·{' '}
                              {formatMoney(alternative.customer_cost)} customer
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : null}

          </>
        )}
      </div>
    </div>
  )
}

function DataTab() {
  const origins = useShippingOrigins()
  const normalize = useNormalizeShippingOrigins()
  const createOrigin = useCreateShippingOrigin()
  const upsertOverride = useUpsertShippingOverride()
  const [originForm, setOriginForm] = useState({
    code: '',
    name: '',
    manufacturerId: '',
    address1: '',
    city: '',
    state: '',
    postalCode: '',
    consolidationKey: '',
  })
  const [override, setOverride] = useState({
    productId: '',
    mode: 'auto',
    originId: '',
    stackable: 'auto',
    packages: '1',
    length: '',
    width: '',
    height: '',
    weight: '',
    freightClass: '',
    notes: '',
  })

  return (
    <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" /> Normalized shipping origins
              </CardTitle>
              <CardDescription>
                Matching ZIPs do not consolidate unless they share an explicit dock key.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={normalize.isPending}
              onClick={async () => {
                try {
                  const result = await normalize.mutateAsync(undefined)
                  toast.success(
                    `Origins normalized: ${result?.created ?? 0} created, ${result?.updated ?? 0} updated`,
                  )
                } catch (error) {
                  toast.error(errorMessage(error))
                }
              }}
            >
              <RefreshCw className={normalize.isPending ? 'animate-spin' : ''} /> Normalize known
              addresses
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-h-[560px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Origin</TableHead>
                  <TableHead>Dock key</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Confidence</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(origins.data ?? []).map((origin) => (
                  <TableRow key={origin.id ?? origin.code}>
                    <TableCell>
                      <p className="font-medium">{origin.name}</p>
                      <p className="text-xs text-muted-foreground">{origin.code}</p>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{origin.consolidation_key}</TableCell>
                    <TableCell>
                      {origin.city}, {origin.state} {origin.postal_code}
                    </TableCell>
                    <TableCell>{statusBadge(origin.confidence)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Add verified dock</CardTitle>
            <CardDescription>
              Use verified city/state/address data for a product-specific ZIP.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <InlineFieldInput
              placeholder="Unique code"
              value={originForm.code}
              onChange={(event) => setOriginForm({ ...originForm, code: event.target.value })}
            />
            <InlineFieldInput
              placeholder="Display name"
              value={originForm.name}
              onChange={(event) => setOriginForm({ ...originForm, name: event.target.value })}
            />
            <InlineFieldInput
              type="number"
              placeholder="Manufacturer DB ID"
              value={originForm.manufacturerId}
              onChange={(event) =>
                setOriginForm({
                  ...originForm,
                  manufacturerId: event.target.value,
                })
              }
            />
            <InlineFieldInput
              placeholder="Address"
              value={originForm.address1}
              onChange={(event) => setOriginForm({ ...originForm, address1: event.target.value })}
            />
            <InlineFieldInput
              placeholder="City"
              value={originForm.city}
              onChange={(event) => setOriginForm({ ...originForm, city: event.target.value })}
            />
            <InlineFieldInput
              placeholder="State"
              maxLength={2}
              value={originForm.state}
              onChange={(event) =>
                setOriginForm({
                  ...originForm,
                  state: event.target.value.toUpperCase(),
                })
              }
            />
            <InlineFieldInput
              placeholder="Postal code"
              value={originForm.postalCode}
              onChange={(event) => setOriginForm({ ...originForm, postalCode: event.target.value })}
            />
            <InlineFieldInput
              placeholder="Dock / consolidation key"
              value={originForm.consolidationKey}
              onChange={(event) =>
                setOriginForm({
                  ...originForm,
                  consolidationKey: event.target.value,
                })
              }
            />
            <Button
              className="sm:col-span-2"
              disabled={
                createOrigin.isPending ||
                !originForm.code ||
                !originForm.name ||
                !originForm.city ||
                !originForm.state ||
                !originForm.postalCode ||
                !originForm.consolidationKey
              }
              onClick={async () => {
                try {
                  await createOrigin.mutateAsync({
                    code: originForm.code,
                    name: originForm.name,
                    manufacturer_id: originForm.manufacturerId
                      ? Number(originForm.manufacturerId)
                      : null,
                    address1: originForm.address1 || null,
                    address2: null,
                    city: originForm.city,
                    state: originForm.state,
                    postal_code: originForm.postalCode,
                    country: 'US',
                    consolidation_key: originForm.consolidationKey,
                    source: 'operator',
                    confidence: 'exact',
                    active: true,
                  })
                  toast.success('Verified dock added')
                  setOriginForm({
                    code: '',
                    name: '',
                    manufacturerId: '',
                    address1: '',
                    city: '',
                    state: '',
                    postalCode: '',
                    consolidationKey: '',
                  })
                } catch (error) {
                  toast.error(errorMessage(error))
                }
              }}
            >
              <Plus /> Add origin
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Product shipping override</CardTitle>
            <CardDescription>Exact operator values always outrank inferred data.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <InlineFieldInput
              className="h-9 sm:col-span-2"
              type="number"
              placeholder="Internal product DB ID"
              value={override.productId}
              onChange={(event) => setOverride({ ...override, productId: event.target.value })}
            />
            <Select
              value={override.mode}
              onValueChange={(mode) => setOverride({ ...override, mode })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="parcel">Parcel</SelectItem>
                <SelectItem value="ltl">LTL</SelectItem>
                <SelectItem value="exclude">Exclude</SelectItem>
              </SelectContent>
            </Select>
            <SearchSelect
              ariaLabel="Origin"
              className="h-9 w-full"
              value={override.originId || undefined}
              allLabel="Automatic origin"
              options={(origins.data ?? []).flatMap((origin) =>
                origin.id != null ? [{ value: String(origin.id), label: origin.name }] : [],
              )}
              onChange={(originId) => setOverride({ ...override, originId: originId ?? '' })}
              searchPlaceholder="Search origins"
            />
            <Select
              value={override.stackable}
              onValueChange={(stackable) => setOverride({ ...override, stackable })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatic stackability</SelectItem>
                <SelectItem value="yes">Stackable</SelectItem>
                <SelectItem value="no">Not stackable</SelectItem>
              </SelectContent>
            </Select>
            {(['packages', 'length', 'width', 'height', 'weight', 'freightClass'] as const).map(
              (field) => (
                <InlineFieldInput
                  key={field}
                  className="h-9"
                  type={field === 'freightClass' ? 'text' : 'number'}
                  placeholder={
                    field === 'packages'
                      ? 'Packages per unit'
                      : field === 'freightClass'
                        ? 'Freight class'
                        : `${formatStatus(field)} (in/lb)`
                  }
                  value={override[field]}
                  onChange={(event) => setOverride({ ...override, [field]: event.target.value })}
                />
              ),
            )}
            <InlineFieldTextarea
              className="sm:col-span-2"
              placeholder="Operator notes"
              value={override.notes}
              onChange={(event) => setOverride({ ...override, notes: event.target.value })}
            />
            <Button
              className="sm:col-span-2"
              disabled={!override.productId || upsertOverride.isPending}
              onClick={async () => {
                try {
                  await upsertOverride.mutateAsync({
                    productId: Number(override.productId),
                    body: {
                      mode_override: override.mode,
                      origin_id: override.originId ? Number(override.originId) : null,
                      packages_per_unit: Number(override.packages || 1),
                      stackable:
                        override.stackable === 'auto' ? null : override.stackable === 'yes',
                      length_in: override.length || null,
                      width_in: override.width || null,
                      height_in: override.height || null,
                      weight_lbs: override.weight || null,
                      freight_class: override.freightClass || null,
                      notes: override.notes || null,
                    },
                  })
                  toast.success('Product shipping override saved')
                } catch (error) {
                  toast.error(errorMessage(error))
                }
              }}
            >
              <Save /> Save override
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function FallbackHistoryTab({ policy }: { policy?: ShippingPolicy }) {
  const cards = useShippingRateCards()
  const attempts = useShippingQuoteAttempts(200)
  const createCard = useCreateShippingRateCard()
  const rebuildCard = useRebuildShippingRateCard()
  const publishCard = usePublishShippingRateCard()
  const sourceMetrics = useMemo(() => {
    const totals = new Map<string, number>()
    for (const attempt of attempts.data ?? []) {
      totals.set(attempt.result_source, (totals.get(attempt.result_source) ?? 0) + 1)
    }
    return [...totals.entries()].sort((left, right) => right[1] - left[1])
  }, [attempts.data])

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>Fallback rate cards</CardTitle>
                <CardDescription>
                  Built from selected WWEX shipment quotes, controlled live simulator runs, and
                  optional fixtures.
                </CardDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={createCard.isPending}
                onClick={async () => {
                  try {
                    await createCard.mutateAsync({
                      name: `P${policy?.fallback_percentile ?? 90} fallback calibration`,
                      percentile: policy?.fallback_percentile ?? 90,
                      region_code: 'US_CONTIGUOUS',
                    })
                    toast.success('Draft fallback card created')
                  } catch (error) {
                    toast.error(errorMessage(error))
                  }
                }}
              >
                <Plus /> New P{policy?.fallback_percentile ?? 90} card
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(cards.data ?? []).map((card) => {
              const coverage = Number(card.validation_metrics?.holdout_coverage ?? 0)
              const holdoutObservations = Number(card.validation_metrics?.holdout_observations ?? 0)
              return (
                <div key={card.id ?? card.version} className="rounded-lg border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          v{card.version} · {card.name}
                        </p>
                        {statusBadge(card.status)}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        P{card.percentile} · {formatNumber(card.observation_count)} observations ·{' '}
                        {(coverage * 100).toFixed(1)}% holdout coverage across{' '}
                        {formatNumber(holdoutObservations)} holdout quotes
                      </p>
                    </div>
                    {card.status === 'draft' && card.id != null && (
                      <div className="flex gap-2">
                        <Button
                          size="xs"
                          variant="outline"
                          disabled={rebuildCard.isPending}
                          onClick={async () => {
                            try {
                              await rebuildCard.mutateAsync(card.id as number)
                              toast.success('Fallback card rebuilt and validated')
                            } catch (error) {
                              toast.error(errorMessage(error))
                            }
                          }}
                        >
                          <RefreshCw /> Build
                        </Button>
                        <Button
                          size="xs"
                          disabled={
                            publishCard.isPending ||
                            coverage < card.percentile / 100 ||
                            holdoutObservations < 20
                          }
                          onClick={async () => {
                            try {
                              await publishCard.mutateAsync(card.id as number)
                              toast.success('Fallback card published')
                            } catch (error) {
                              toast.error(errorMessage(error))
                            }
                          }}
                        >
                          Publish
                        </Button>
                      </div>
                    )}
                  </div>
                  <Progress className="mt-3" value={coverage * 100} />
                </div>
              )
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rate-source mix</CardTitle>
            <CardDescription>
              Last {attempts.data?.length ?? 0} sanitized checkout and simulator calculations.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sourceMetrics.length ? (
              sourceMetrics.map(([source, count]) => (
                <div key={source} className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">{formatStatus(source)}</p>
                  <p className="mt-1 text-2xl font-semibold">{count}</p>
                  <p className="text-xs text-muted-foreground">
                    {((count / (attempts.data?.length || 1)) * 100).toFixed(1)}%
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No quote history yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quote history and booked-cost reconciliation</CardTitle>
          <CardDescription>
            No names, street addresses, phone numbers, or emails are retained.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="hidden max-h-[540px] overflow-auto md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Fingerprint</TableHead>
                  <TableHead>Request</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Subtotal</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Booked</TableHead>
                  <TableHead>Latency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(attempts.data ?? []).map((attempt) => (
                  <TableRow key={attempt.id ?? attempt.uuid ?? attempt.request_fingerprint}>
                    <TableCell className="whitespace-nowrap text-xs">
                      <QuoteAttemptLink
                        attemptId={attempt.id}
                        className="font-medium text-primary hover:underline"
                      >
                        {new Date(attempt.created_at).toLocaleString()}
                      </QuoteAttemptLink>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <QuoteAttemptLink
                        attemptId={attempt.id}
                        className="text-primary hover:underline"
                      >
                        {attempt.request_fingerprint.slice(0, 12)}
                      </QuoteAttemptLink>
                      {attempt.shopify_order_id ? (
                        <Badge className="ml-2" variant="outline">
                          matched
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>{statusBadge(attempt.request_source)}</TableCell>
                    <TableCell>{statusBadge(attempt.result_source)}</TableCell>
                    <TableCell>{formatMoney(attempt.discounted_subtotal)}</TableCell>
                    <TableCell>{formatMoney(attempt.base_cost)}</TableCell>
                    <TableCell>{formatMoney(attempt.customer_cost)}</TableCell>
                    <TableCell>{formatMoney(attempt.booked_cost)}</TableCell>
                    <TableCell>{attempt.duration_ms} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="space-y-2 md:hidden">
            {(attempts.data ?? []).map((attempt) => (
              <QuoteAttemptLink
                key={attempt.id}
                attemptId={attempt.id}
                className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-xs font-medium">
                      {attempt.request_fingerprint.slice(0, 12)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {new Date(attempt.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {statusBadge(attempt.request_source)}
                    {statusBadge(attempt.result_source)}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 border-t pt-3 text-xs">
                  <div>
                    <p className="text-muted-foreground">Provider</p>
                    <p className="mt-0.5 font-medium tabular-nums">{formatMoney(attempt.base_cost)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Customer</p>
                    <p className="mt-0.5 font-medium tabular-nums">{formatMoney(attempt.customer_cost)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-muted-foreground">Latency</p>
                    <p className="mt-0.5 font-medium tabular-nums">{attempt.duration_ms} ms</p>
                  </div>
                </div>
              </QuoteAttemptLink>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function ShippingCalculatorPage() {
  const policies = useShippingPolicies()
  const [selectedPolicyId, setSelectedPolicyId] = useState<number | null>(null)
  const selectedPolicy = useMemo(() => {
    const rows = policies.data ?? []
    if (selectedPolicyId != null) return rows.find((policy) => policy.id === selectedPolicyId)
    return (
      rows.find((policy) => policy.status === 'draft') ??
      rows.find((policy) => policy.status === 'published') ??
      rows[0]
    )
  }, [policies.data, selectedPolicyId])

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Truck className="h-6 w-6" /> Shopify Shipping Calculator
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            One blended Commercial Delivery rate, with explainable parcel, LTL, freight-rule, and
            fallback decisions.
          </p>
        </div>
        <SearchSelect
          ariaLabel="Policy version"
          className="h-9 w-[250px]"
          value={selectedPolicy?.id != null ? String(selectedPolicy.id) : undefined}
          options={(policies.data ?? []).map((policy) => ({
            value: String(policy.id),
            label: `v${policy.version} · ${formatStatus(policy.status)} · P${policy.product_inference_percentile}/P${policy.fallback_percentile}`,
          }))}
          onChange={(value) => value && setSelectedPolicyId(Number(value))}
          placeholder="Select a policy version"
          searchPlaceholder="Search versions"
        />
      </div>

      <Tabs defaultValue="policy">
        <div className="overflow-x-auto">
          <TabsList className="grid w-full grid-cols-2 group-data-[orientation=horizontal]/tabs:h-auto sm:flex sm:w-max sm:min-w-full sm:justify-start sm:group-data-[orientation=horizontal]/tabs:h-9">
            <TabsTrigger value="policy">
              <Settings2 /> Policy & readiness
            </TabsTrigger>
            <TabsTrigger value="simulator">
              <Play /> Quote simulator
            </TabsTrigger>
            <TabsTrigger value="data">
              <Database /> Origins & overrides
            </TabsTrigger>
            <TabsTrigger value="fallback">
              <ShieldCheck /> Fallback & history
            </TabsTrigger>
            <TabsTrigger value="shipped">
              <Ruler /> Shipped vs quoted
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="policy">
          <PolicyTab key={selectedPolicy?.id ?? 'none'} selectedPolicy={selectedPolicy} />
        </TabsContent>
        <TabsContent value="simulator">
          <SimulatorTab policy={selectedPolicy} />
        </TabsContent>
        <TabsContent value="data">
          <DataTab />
        </TabsContent>
        <TabsContent value="fallback">
          <FallbackHistoryTab policy={selectedPolicy} />
        </TabsContent>
        <TabsContent value="shipped">
          <ShipmentAccuracyTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
